import { GeneratorInstanceDto } from '@services/dtos'
import { Tooltip, useTheme } from '@mui/material'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import moment from 'moment'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useEffect } from 'react'
import { getGeneratorInstance } from '@state/reducers/generatorInstanceReducer'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'

interface Props {
  generatorInstance: GeneratorInstanceDto & { slotName?: string }
  selected?: boolean
  onSelect?: (generatorInstance: GeneratorInstanceDto) => void
  displayLastElutionDate?: boolean
  refreshTheoreticalActivity?: boolean
}

export function GeneratorInstanceCard({ 
  generatorInstance, 
  selected, 
  onSelect, 
  displayLastElutionDate = true,
  refreshTheoreticalActivity = true
}: Props) {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const updatedInstance = useAppSelector(
    (state) => state.generatorInstance.instancesById[generatorInstance.id]
  )
  const theoreticalActivityAtTimeMbq = updatedInstance?.theoreticalActivityAtTimeMbq 
    ?? generatorInstance.theoreticalActivityAtTimeMbq

  useEffect(() => {
    if (refreshTheoreticalActivity) {
      const interval = setInterval(() => {
        dispatch(getGeneratorInstance(generatorInstance.id))
      }, 30_000)
      return () => {
        clearInterval(interval)
      }
    }
  }, [generatorInstance.id])
  
  return (
    <div 
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border bg-white w-fit transition-all
        ${onSelect ? 'cursor-pointer hover:border-gray-300' : ''}`}
      style={selected ? {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}0d`,
        boxShadow: `0 1px 2px ${theme.palette.primary.main}33`,
      } : { borderColor: '#e5e7eb' }}
      onClick={() => generatorInstance && onSelect?.(generatorInstance)}>
      {generatorInstance ? (
        <>
          <div className="flex flex-col gap-0.5">
            <Tooltip title="Numéro de série" arrow>
              <span className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 w-fit">
                <HubOutlinedIcon sx={{ fontSize: 13 }} className="text-slate-400" />
                <span className="font-mono text-xs font-semibold tracking-wide text-slate-600">
                  {generatorInstance.serialNumber}
                </span>
              </span>
            </Tooltip>
            {displayLastElutionDate && <span className="text-xs text-slate-400">
              Dernière élution : {generatorInstance.lastElutionDate ? moment(generatorInstance.lastElutionDate).format('DD/MM/YYYY HH:mm') : 'N/A'}
            </span>}
            {theoreticalActivityAtTimeMbq ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-slate-500">Activité max attendue :</span>
                <span className="text-[13px] font-bold text-slate-800">
                  {theoreticalActivityAtTimeMbq} MBq
                </span>
                <Tooltip title="Recalculée toutes les 30s" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'pointer' }} />
                </Tooltip>
              </div>
            ) : (
              <span className="text-xs text-slate-400">Activité max attendue : N/A</span>
            )}
          </div>

          <div className="w-px h-7 bg-gray-200 mx-1" />

          <div>
            <div className="text-xs font-semibold text-slate-700">
              {generatorInstance.productBatch.productCatalog.name}
            </div>
            {(generatorInstance.productBatch.productCatalog.generatorParentIsotope || generatorInstance.productBatch.productCatalog.generatorDaughterIsotope) && (
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <span>{generatorInstance.productBatch.productCatalog.generatorParentIsotope?.name}</span>
                <span>→</span>
                <span>{generatorInstance.productBatch.productCatalog.generatorDaughterIsotope?.name}</span>
              </div>
            )}    
            <div className="text-xs text-slate-500">
              Lot <span className="font-semibold">{generatorInstance.productBatch.lotNumber}</span>
            </div>
            {generatorInstance.productBatch.expiryAt && <div className="text-xs text-gray-400">
              Exp. {moment(generatorInstance.productBatch.expiryAt).format('DD/MM/YYYY')}
            </div>}
            {generatorInstance.slotName && <div className="text-xs text-slate-400">Emplacement {generatorInstance.slotName}</div>}
          </div>
        </>
      ) : (
        <span className="text-xs text-gray-300 italic">Aucun générateur affecté</span>
      )}
    </div>
  )
}
